import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';
import type { PDI, CreatePDIInput } from '../types';
import { toast } from '@/shared/lib/toast';

/**
 * Hook para buscar PDIs
 */
export function usePDIs(filters?: {
  employeeId?: string;
  quarter?: number;
  year?: number;
  status?: 'ACTIVE' | 'CLOSED';
}) {
  return useQuery({
    queryKey: ['feedback-pdi', 'pdis', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.employeeId) params.append('employeeId', filters.employeeId);
      if (filters?.quarter) params.append('quarter', filters.quarter.toString());
      if (filters?.year) params.append('year', filters.year.toString());
      if (filters?.status) params.append('status', filters.status);

      const response = await api.get<{ pdis: PDI[]; total: number }>(
        `/feedback-pdi/pdis?${params.toString()}`
      );

      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Hook para buscar um PDI específico
 */
export function usePDI(id?: string) {
  return useQuery({
    queryKey: ['feedback-pdi', 'pdis', id],
    queryFn: async () => {
      if (!id) return null;

      const response = await api.get<{ pdi: PDI }>(`/feedback-pdi/pdis/${id}`);

      return response.data.pdi;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Hook para criar PDI
 */
export function useCreatePDI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePDIInput) => {
      const response = await api.post<{ pdi: PDI }>('/feedback-pdi/pdis', data);
      return response.data.pdi;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'pdis'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'dashboard'] });
      toast.success('PDI criado com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao criar PDI');
    },
  });
}

/**
 * Hook para atualizar objetivo do PDI
 */
export function useUpdatePDIObjective() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        title?: string;
        action?: string;
        successIndicator?: string;
        deadline?: string;
        supportNeeded?: string;
        status?: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'ABORTED';
      };
    }) => {
      const response = await api.put(`/feedback-pdi/objectives/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'pdis'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'dashboard'] });
      toast.success('Objetivo atualizado!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao atualizar objetivo');
    },
  });
}

/**
 * Hook para encerrar PDI
 */
export function useClosePDI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        employeeSelfAssessment: string;
        managerAssessment: string;
        finalScore: number;
      };
    }) => {
      const response = await api.post(`/feedback-pdi/pdis/${id}/close`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'pdis'] });
      queryClient.invalidateQueries({ queryKey: ['feedback-pdi', 'dashboard'] });
      toast.success('PDI encerrado com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Erro ao encerrar PDI');
    },
  });
}
